import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  MessageSquare, Plus, Send, Users, Calendar, Pin, Trash2,
  Edit, Eye, Clock, CheckCircle, AlertCircle, Loader2,
  Megaphone, School, User, Bell, Mail
} from 'lucide-react';
import api from '../lib/api';

const MuralComunicados = () => {
  const [comunicados, setComunicados] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [enviando, setEnviando] = useState(false);
  
  const [formData, setFormData] = useState({
    titulo: '',
    conteudo: '',
    tipo: 'informativo',
    prioridade: 'normal',
    destinatarios: 'todos',
    turma_id: '',
    fixado: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [comunicadosRes, turmasRes] = await Promise.all([
        api.get('/mural/comunicados'),
        api.get('/classes'),
      ]);
      setComunicados(comunicadosRes.data || []);
      setTurmas(turmasRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setComunicados([]);
    } finally {
      setLoading(false);
    }
  };

  const abrirNovoModal = () => {
    setEditingId(null);
    setFormData({
      titulo: '',
      conteudo: '',
      tipo: 'informativo',
      prioridade: 'normal',
      destinatarios: 'todos',
      turma_id: '',
      fixado: false,
    });
    setShowModal(true);
  };

  const abrirEditarModal = (comunicado) => {
    setEditingId(comunicado.id);
    setFormData({
      titulo: comunicado.titulo,
      conteudo: comunicado.conteudo,
      tipo: comunicado.tipo,
      prioridade: comunicado.prioridade,
      destinatarios: comunicado.destinatarios,
      turma_id: comunicado.turma_id || '',
      fixado: comunicado.fixado || false,
    });
    setShowModal(true);
  };

  const salvarComunicado = async () => {
    if (!formData.titulo || !formData.conteudo) {
      toast.error('Preencha título e conteúdo');
      return;
    }

    setEnviando(true);
    try {
      if (editingId) {
        await api.put(`/mural/comunicados/${editingId}`, formData);
        toast.success('Comunicado atualizado!');
      } else {
        await api.post('/mural/comunicados', formData);
        toast.success('Comunicado publicado!');
      }
      loadData();
      setShowModal(false);
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar comunicado');
    } finally {
      setEnviando(false);
    }
  };

  const excluirComunicado = async (id) => {
    if (!confirm('Deseja realmente excluir este comunicado?')) return;
    
    try {
      await api.delete(`/mural/comunicados/${id}`);
      toast.success('Comunicado excluído!');
      loadData();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error('Erro ao excluir comunicado');
    }
  };

  const enviarPorEmail = async (comunicado) => {
    try {
      await api.post(`/mural/comunicados/${comunicado.id}/enviar-email`);
      toast.success('Comunicado enviado por e-mail!');
    } catch (error) {
      console.error('Erro ao enviar:', error);
      toast.error('Erro ao enviar por e-mail');
    }
  };

  const toggleFixado = async (comunicado) => {
    try {
      await api.patch(`/mural/comunicados/${comunicado.id}/fixar`, {
        fixado: !comunicado.fixado
      });
      loadData();
      toast.success(comunicado.fixado ? 'Comunicado desafixado' : 'Comunicado fixado!');
    } catch (error) {
      console.error('Erro ao fixar:', error);
    }
  };

  const getTipoConfig = (tipo) => {
    const configs = {
      informativo: { icon: MessageSquare, color: 'bg-blue-100 text-blue-700', label: 'Informativo' },
      importante: { icon: AlertCircle, color: 'bg-orange-100 text-orange-700', label: 'Importante' },
      urgente: { icon: Bell, color: 'bg-red-100 text-red-700', label: 'Urgente' },
      evento: { icon: Calendar, color: 'bg-purple-100 text-purple-700', label: 'Evento' },
      lembrete: { icon: Clock, color: 'bg-green-100 text-green-700', label: 'Lembrete' },
    };
    return configs[tipo] || configs.informativo;
  };

  const getPrioridadeConfig = (prioridade) => {
    const configs = {
      baixa: { color: 'bg-slate-100 text-slate-600', label: 'Baixa' },
      normal: { color: 'bg-blue-100 text-blue-600', label: 'Normal' },
      alta: { color: 'bg-orange-100 text-orange-600', label: 'Alta' },
      urgente: { color: 'bg-red-100 text-red-600', label: 'Urgente' },
    };
    return configs[prioridade] || configs.normal;
  };

  const comunicadosFixados = comunicados.filter(c => c.fixado);
  const comunicadosNormais = comunicados.filter(c => !c.fixado);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-purple-600" />
            Mural de Comunicados
          </h1>
          <p className="text-muted-foreground mt-1">
            Publique comunicados para alunos e turmas
          </p>
        </div>
        
        <Button onClick={abrirNovoModal} className="gap-2 bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4" />
          Novo Comunicado
        </Button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-2xl font-bold">{comunicados.length}</p>
                <p className="text-sm opacity-80">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Pin className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-2xl font-bold">{comunicadosFixados.length}</p>
                <p className="text-sm opacity-80">Fixados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-2xl font-bold">{comunicados.filter(c => c.tipo === 'urgente').length}</p>
                <p className="text-sm opacity-80">Urgentes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-2xl font-bold">{comunicados.filter(c => c.enviado_email).length}</p>
                <p className="text-sm opacity-80">Enviados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Comunicados Fixados */}
      {comunicadosFixados.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Pin className="w-5 h-5 text-orange-500" />
            Comunicados Fixados
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {comunicadosFixados.map(comunicado => (
              <ComunicadoCard 
                key={comunicado.id}
                comunicado={comunicado}
                turmas={turmas}
                onEdit={abrirEditarModal}
                onDelete={excluirComunicado}
                onTogglePin={toggleFixado}
                onSendEmail={enviarPorEmail}
                getTipoConfig={getTipoConfig}
                getPrioridadeConfig={getPrioridadeConfig}
              />
            ))}
          </div>
        </div>
      )}

      {/* Comunicados Recentes */}
      <div>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-slate-500" />
          Comunicados Recentes
        </h2>
        
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" />
            <p className="text-slate-500 mt-2">Carregando comunicados...</p>
          </div>
        ) : comunicadosNormais.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed">
            <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nenhum comunicado publicado ainda</p>
            <Button onClick={abrirNovoModal} variant="link" className="text-purple-600 mt-2">
              Criar primeiro comunicado
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {comunicadosNormais.map(comunicado => (
              <ComunicadoCard 
                key={comunicado.id}
                comunicado={comunicado}
                turmas={turmas}
                onEdit={abrirEditarModal}
                onDelete={excluirComunicado}
                onTogglePin={toggleFixado}
                onSendEmail={enviarPorEmail}
                getTipoConfig={getTipoConfig}
                getPrioridadeConfig={getPrioridadeConfig}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal de Criar/Editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingId ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {editingId ? 'Editar Comunicado' : 'Novo Comunicado'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input
                value={formData.titulo}
                onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                placeholder="Título do comunicado..."
              />
            </div>

            <div className="space-y-2">
              <Label>Conteúdo *</Label>
              <Textarea
                value={formData.conteudo}
                onChange={(e) => setFormData({...formData, conteudo: e.target.value})}
                placeholder="Digite o conteúdo do comunicado..."
                className="min-h-[150px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData({...formData, tipo: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="informativo">📋 Informativo</SelectItem>
                    <SelectItem value="importante">⚠️ Importante</SelectItem>
                    <SelectItem value="urgente">🚨 Urgente</SelectItem>
                    <SelectItem value="evento">📅 Evento</SelectItem>
                    <SelectItem value="lembrete">⏰ Lembrete</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Prioridade</Label>
                <Select value={formData.prioridade} onValueChange={(v) => setFormData({...formData, prioridade: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Destinatários</Label>
                <Select value={formData.destinatarios} onValueChange={(v) => setFormData({...formData, destinatarios: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">👥 Todos os alunos</SelectItem>
                    <SelectItem value="turma">🏫 Turma específica</SelectItem>
                    <SelectItem value="professores">👨‍🏫 Apenas professores</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.destinatarios === 'turma' && (
                <div className="space-y-2">
                  <Label>Turma</Label>
                  <Select value={formData.turma_id} onValueChange={(v) => setFormData({...formData, turma_id: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {turmas.map(turma => (
                        <SelectItem key={turma.id} value={turma.id}>
                          {turma.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="fixado"
                checked={formData.fixado}
                onChange={(e) => setFormData({...formData, fixado: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="fixado" className="cursor-pointer">
                📌 Fixar este comunicado no topo
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={salvarComunicado} 
              disabled={enviando}
              className="gap-2 bg-purple-600 hover:bg-purple-700"
            >
              {enviando ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  {editingId ? 'Atualizar' : 'Publicar'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Componente de Card do Comunicado
const ComunicadoCard = ({ 
  comunicado, 
  turmas, 
  onEdit, 
  onDelete, 
  onTogglePin, 
  onSendEmail,
  getTipoConfig,
  getPrioridadeConfig 
}) => {
  const tipoConfig = getTipoConfig(comunicado.tipo);
  const prioridadeConfig = getPrioridadeConfig(comunicado.prioridade);
  const TipoIcon = tipoConfig.icon;
  const turma = turmas.find(t => t.id === comunicado.turma_id);

  return (
    <Card className={`overflow-hidden hover:shadow-lg transition-shadow ${comunicado.fixado ? 'border-2 border-orange-300' : ''}`}>
      <div className={`px-4 py-3 ${tipoConfig.color}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TipoIcon className="w-4 h-4" />
            <span className="font-medium text-sm">{tipoConfig.label}</span>
          </div>
          <div className="flex items-center gap-2">
            {comunicado.fixado && (
              <Pin className="w-4 h-4 text-orange-600" />
            )}
            <Badge variant="secondary" className={prioridadeConfig.color}>
              {prioridadeConfig.label}
            </Badge>
          </div>
        </div>
      </div>

      <CardContent className="p-4">
        <h3 className="font-bold text-lg mb-2">{comunicado.titulo}</h3>
        <p className="text-slate-600 text-sm line-clamp-3 mb-4">{comunicado.conteudo}</p>

        <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            {comunicado.autor || 'Professor'}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {comunicado.data_criacao ? new Date(comunicado.data_criacao).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : 'Hoje'}
          </span>
          {comunicado.destinatarios === 'turma' && turma && (
            <span className="flex items-center gap-1">
              <School className="w-3 h-3" />
              {turma.name}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => onEdit(comunicado)}>
            <Edit className="w-3 h-3 mr-1" />
            Editar
          </Button>
          <Button variant="outline" size="sm" onClick={() => onTogglePin(comunicado)}>
            <Pin className="w-3 h-3 mr-1" />
            {comunicado.fixado ? 'Desafixar' : 'Fixar'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => onSendEmail(comunicado)}>
            <Mail className="w-3 h-3 mr-1" />
            Enviar
          </Button>
          <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => onDelete(comunicado.id)}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default MuralComunicados;
